import React, { forwardRef } from 'react';
import { styled, Typography } from '@mui/material';

import { useViewport } from 'contexts/viewport';
import Box from 'shell/components/Box';
import Tooltip from 'shell/components/Tooltip';
import theme from 'shell/themes';
import { getTooltipProps, stripHTML } from 'utils/helper';

const COLORS = ['default', 'disabled', 'error', 'inherit', 'light', 'primary', 'success'];
const VARIANTS = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'sh1', 'sh2', 'p1', 'p2', 'p3', 'p4'];

const CustomTypography = styled(Typography)(({ $color, size, variant, weight, theme }) => {
    const defaultWeight = Object.keys(theme.typography[variant].fontWeight)[0];

    const colorMap = {
        default: theme.palette.ensightful.neutral[900],
        disabled: theme.palette.ensightful.neutral[400],
        error: theme.palette.ensightful.error[600],
        inherit: 'inherit',
        light: theme.palette.ensightful.neutral[700],
        primary: theme.palette.ensightful.primary[500],
        success: theme.palette.ensightful.success[700],
    };

    const color = Object.keys(colorMap).includes($color) ? colorMap[$color] : $color;

    return {
        maxWidth: '100%',
        fontFamily: 'inherit',
        fontSize: `${theme.typography[variant].fontSize[size]}rem`,
        fontWeight: theme.typography[variant].fontWeight[weight || defaultWeight],
        letterSpacing: `${theme.typography[variant].letterSpacing[size]}rem`,
        lineHeight: `${theme.typography.getLineHeight(variant, size)}rem`,
        hyphens: 'auto',
        wordWrap: 'break-word',
        color,
        '& p': {
            marginBottom: 0,
            whiteSpace: 'pre-wrap',
        },
    };
});

const RichTextBox = styled(Box)(({ noWrap, theme }) => {
    return {
        whiteSpace: 'pre',
        textWrap: 'wrap',
        '& p': {
            minHeight: '1rem',
        },
        '& a': {
            color: theme.palette.ensightful.primary[500],
            textDecoration: 'underline',
            transition: 'none',
            '&:hover': {
                color: theme.palette.ensightful.primary[700],
                textDecoration: 'underline',
            },
            '&:active': {
                color: theme.palette.ensightful.primary[800],
                textDecoration: 'underline',
            },
        },
        ...(noWrap && {
            display: 'inline-block',
            width: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            textWrap: 'unset',
            whiteSpace: 'nowrap',
        }),
    };
});

const trunc = (string, n) => {
    if (string.length <= n) return string;
    const substring = string.slice(0, n - 1);
    return <>{substring.slice(0, substring.lastIndexOf(' '))}&hellip;</>;
};

export default forwardRef(
    ({ children, color = 'default', rich = false, tooltip, truncate, variant = 'p2', ...rest }, ref) => {
        if (tooltip && !tooltip.component)
            throw new Error(
                `Shell: Invalid tooltip in component Text. Expected "tooltip.component" to be "string" or "ReactElement", but got "${typeof tooltip.component}" instead.`
            );
        if (!COLORS.includes(color) && typeof color !== 'function') {
            throw new Error(`Shell: Invalid color "${color}" in component Text.`);
        }
        if (!VARIANTS.includes(variant)) throw new Error(`Shell: Invalid variant "${variant}" in component Text.`);

        const { device } = useViewport();

        if (typeof color === 'function') color = color(theme);
        const size = device === 'mobile' ? 'small' : 'large';

        if (typeof children === 'string' && truncate) children = trunc(children, truncate);

        if (rich && rest.noWrap) children = stripHTML(children);

        const TextComponent = (
            <CustomTypography ref={ref} $color={color} size={size} variant={variant} {...rest}>
                {rich ? (
                    <RichTextBox
                        component={'span'}
                        dangerouslySetInnerHTML={{ __html: children }}
                        noWrap={rest.noWrap}
                    />
                ) : (
                    children
                )}
            </CustomTypography>
        );

        return tooltip ? <Tooltip {...getTooltipProps(tooltip)}>{TextComponent}</Tooltip> : TextComponent;
    }
);
